import cx from "classnames";
import { useCallback, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import CodeChanges from "components/CodeChanges";
import { CodeChangesGroupNode } from "components/CodeChanges/types";
import { ArrowRight } from "components/icons/generated";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import TextEllipsis from "ds/components/TextEllipsis";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import { EntityChangeType } from "types/generated";
import { normalizePath, populateChangeTree } from "components/Run/elements/ResourcesDiff/helpers";
import ChangeTypeIcon from "components/Run/elements/ChangeTypeIcon";
import { getChangeType } from "components/Run/helpers";
import CardCollapsibleWrapper from "ds/components/Card/Collapsible/components/Wrapper";
import CardHeader from "ds/components/Card/components/Header";
import CardCollapsibleToggle from "ds/components/Card/Collapsible/components/Toggle";
import CardTitle from "ds/components/Card/components/Title";
import CardCollapsibleContent from "ds/components/Card/Collapsible/components/Content";
import useTypedFlags from "hooks/useTypedFlags";

import EntityChangeResultBadge from "../EntityChangeResultBadge";
import { EntityChangeWithId } from "../types";
import { useChangesStore } from "../useChangesStore";
import ReplacementIndicator from "./ReplacementIndicator";
import styles from "./styles.module.css";
import RunChangesResourcesDiff from "../../ResourcesDiff";

type RunChangesListItemProps = {
  item: EntityChangeWithId;
  onCheckItem: (item: EntityChangeWithId, checked: boolean) => unknown;
  checked: boolean;
  selectable: boolean;
};

const RunChangesListItem = ({
  item,
  onCheckItem,
  checked,
  selectable,
}: RunChangesListItemProps) => {
  const { runChangesDiffImprovements } = useTypedFlags();
  const [editorHeight, setEditorHeight] = useState(0);

  const collapsed = useChangesStore(
    useShallow(({ getChange }) => {
      return getChange(item.id)?.collapsed ?? false;
    })
  );

  const collapsedGroups = useChangesStore(
    useShallow(({ getChange }) => {
      return getChange(item.id)?.collapsedGroups ?? new Set<string>();
    })
  );
  const toggleChange = useChangesStore(
    useShallow(({ toggleChange }) => {
      return toggleChange;
    })
  );

  const updateChangeGroups = useChangesStore(
    useShallow(({ updateChangeGroups }) => {
      return updateChangeGroups;
    })
  );

  const handleCheckItem = useCallback(() => {
    onCheckItem(item, !checked);
  }, [onCheckItem, item, checked]);

  const { moved, result } = item.metadata;

  const changeType = getChangeType(item.metadata);

  const parsedItemsNewCodeChanges = useMemo((): CodeChangesGroupNode => {
    const root: CodeChangesGroupNode = {
      id: "root",
      children: [],
    };

    if (collapsed) return root;

    item.attributeChanges.forEach((change) => {
      const keys = normalizePath(change.path);

      populateChangeTree(change, keys, root);
    });

    return root;
  }, [item, collapsed]);

  const hasNoChanges = parsedItemsNewCodeChanges.children.length === 0;

  return (
    <CardCollapsibleWrapper isCollapsed={collapsed} onToggle={() => toggleChange(item.id)}>
      <CardHeader suffix={<EntityChangeResultBadge type={result} />}>
        <Box align="center" gap="medium">
          <CardCollapsibleToggle
            ariaLevel={3}
            ariaLabel={
              moved
                ? `${item.previousAddress}
                  moves to
                  ${item.address}`
                : item.address
            }
          />

          {selectable && !item.address.startsWith("output.") && (
            <Toggle
              variant="checkbox"
              id={`change-${item.id}`}
              onChange={handleCheckItem}
              isSelected={checked}
              dataTestId="run-changes-list-item-checkbox"
              aria-label={`Select ${item.address} for replanning`}
              value={item.address}
            />
          )}

          <ChangeTypeIcon type={item.metadata.type} moved={item.metadata.moved} />

          <TextEllipsis tooltip={item.address} tooltipWidthMode="maxWidthSm">
            {({ className, ...props }) => (
              <CardTitle tag="span" {...props} className={cx(className, styles.panelTitle)}>
                {moved ? (
                  <>
                    {item.previousAddress}
                    <Icon src={ArrowRight} color="secondary" />
                    {item.address}
                  </>
                ) : (
                  item.address
                )}
              </CardTitle>
            )}
          </TextEllipsis>

          {(changeType === EntityChangeType.ReplaceCreateBeforeDestroy ||
            changeType === EntityChangeType.ReplaceDestroyBeforeCreate) && (
            <ReplacementIndicator changeType={changeType} />
          )}
        </Box>
      </CardHeader>
      {!collapsed && (
        <CardCollapsibleContent hasHeader fullbleed>
          {runChangesDiffImprovements ? (
            <Box fullWidth relative>
              {hasNoChanges && item.metadata.moved ? (
                <Box fullWidth padding="0 x-large x-large x-large">
                  <Typography tag="span">Resource moved without any modifications.</Typography>
                </Box>
              ) : (
                <CodeChanges
                  changes={parsedItemsNewCodeChanges}
                  collapsedGroups={collapsedGroups}
                  onGroupToggle={(collapsedGroups) => updateChangeGroups(item.id, collapsedGroups)}
                />
              )}
            </Box>
          ) : (
            <Box fullWidth relative>
              <RunChangesResourcesDiff
                id={item.id}
                items={item.attributeChanges}
                changeType={changeType}
                editorHeight={editorHeight}
                setEditorHeight={setEditorHeight}
              />
            </Box>
          )}
        </CardCollapsibleContent>
      )}
    </CardCollapsibleWrapper>
  );
};

export default RunChangesListItem;
